/**
 * Row assembly for the score breakdown. Pure and in its own module so it can be
 * tested without mounting, and because `.tsx` files here may only export
 * components (eslint `react-refresh/only-export-components`).
 */
import type { SalesHealthReason, SalesScoreCatalog } from '../../api/types';

export interface BreakdownRow {
  key: string;
  name: string;
  group: string;
  weight: number;
  contribution: number;
  firing: boolean;
  description?: string;
  /** The span the signal is measured over, e.g. "Last week (PW) vs the 4-week
   *  average (W4)". A severity with no stated window is not auditable. */
  windows?: string;
  /** Guards that REDUCED this signal's points, and by how much. Without them the
   *  Severity column reports the damped ratio as if it were the measurement. */
  guards?: Array<{ label: string; factor: number }>;
  /** What 100% severity means for this signal. The column header cannot say it:
   *  eight different formulas render under it. */
  severityBasis?: string;
}

/** `values` keys the scorer writes when a guard fires, in the order they read best. */
const GUARD_LABELS: Array<[string, string]> = [
  ['seasonal_damping', 'seasonal'],
  ['market_damping', 'market down on last year'],
  ['trajectory_damping', 'up on the year'],
];

function guardsOf(reason: SalesHealthReason): Array<{ label: string; factor: number }> {
  const found: Array<{ label: string; factor: number }> = [];
  for (const [key, label] of GUARD_LABELS) {
    const factor = reason.values?.[key];
    if (typeof factor === 'number' && factor > 0 && factor < 1) found.push({ label, factor });
  }
  return found;
}

/** What the signal measured BEFORE any guard reduced it.
 *
 *  `contribution` is weight x severity x every guard factor, so dividing it by weight
 *  alone reports a damped client as a milder one. */
export function rawSeverity(row: BreakdownRow): number | null {
  if (!row.firing || row.weight <= 0) return null;
  const applied = (row.guards ?? []).reduce((product, g) => product * g.factor, 1);
  if (applied <= 0) return null;
  return row.contribution / (row.weight * applied);
}

/** The risk points this signal WOULD have cost with no guard applied.
 *
 *  Without it the guards are visible but their effect is not: a reader has to divide
 *  0.38 by 0.30 in their head to find out what the guard was worth. */
export function undampedContribution(row: BreakdownRow): number | null {
  const severity = rawSeverity(row);
  return severity === null ? null : severity * row.weight;
}

/**
 * One row per SIGNAL SLOT, firing or not.
 *
 * Showing only the signals that fired answers "what is wrong" but not "out of
 * what". A rep reading 39% cannot tell whether that is three signals out of four
 * or three out of thirteen, and the difference decides whether the number is
 * alarming. Dormant signals are therefore listed greyed rather than omitted.
 *
 * Variants matter here: a client whose sell volume went dark emits
 * `SELL_ACUTE_DARK`, which occupies the `SELL_ACUTE` slot. Matching on code alone
 * would render that client as BOTH firing an unrecognised signal and dormant on
 * sell volume -- the same 3 points counted twice and missed once.
 */
export function buildBreakdownRows(
  reasons: SalesHealthReason[],
  catalog?: SalesScoreCatalog | null,
): BreakdownRow[] {
  const firing = reasons.filter((r) => r.code !== 'NO_SIGNALS');

  // Before the catalogue loads, fall back to firing-only rather than guessing at
  // a signal set.
  if (!catalog || catalog.signals.length === 0) {
    return firing.map((r) => ({
      key: r.code,
      name: r.label,
      group: r.group,
      weight: r.weight,
      contribution: r.contribution,
      firing: true,
      guards: guardsOf(r),
    }));
  }

  const slotFor = new Map<string, string>();
  for (const s of catalog.signals) {
    slotFor.set(s.code, s.code);
    for (const v of s.variants) slotFor.set(v, s.code);
  }

  const firedBySlot = new Map<string, SalesHealthReason>();
  const unmatched: SalesHealthReason[] = [];
  for (const r of firing) {
    const slot = slotFor.get(r.code);
    if (slot) firedBySlot.set(slot, r);
    else unmatched.push(r);
  }

  const rows: BreakdownRow[] = catalog.signals.map((s) => {
    const hit = firedBySlot.get(s.code);
    return {
      key: s.code,
      // The server-rendered label carries this client's actual numbers, so it
      // wins over the generic catalogue name whenever the signal fired.
      name: hit ? hit.label : s.name,
      group: s.group,
      weight: s.weight,
      contribution: hit ? hit.contribution : 0,
      firing: Boolean(hit),
      description: s.description,
      windows: s.windows,
      severityBasis: s.severity,
      guards: hit ? guardsOf(hit) : undefined,
    };
  });

  // A signal the catalogue does not know about still contributed risk, so it is
  // appended rather than dropped: silently hiding it would break the sum that
  // makes this table worth showing.
  for (const r of unmatched) {
    rows.push({
      key: r.code,
      name: r.label,
      group: r.group,
      weight: r.weight,
      contribution: r.contribution,
      firing: true,
      guards: guardsOf(r),
    });
  }

  return rows;
}
